// A compact EHR note card: who it is about, date, method, and tags.
import { Fragment, type ReactNode, useState } from 'react';

import { Combobox } from '@apps/web/components/ui/combobox';
import { CommandEmpty, CommandItem } from '@apps/web/components/ui/command';
import { Input } from '@apps/web/components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import {
    EHR_NOTE_METHODS,
    type EhrPersonNoteEditor,
} from '@apps/web/features/phoebe_chat_v3/model/ehr_person_note_editor';
import { type PermissionStatus } from '@apps/web/features/phoebe_chat_v3/registry/definitions/tool_call';
import {
    type PermissionOutcome,
    type WriteResultPresentation,
} from '@apps/web/features/phoebe_chat_v3/registry/types';
import { CompactWriteCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/compact_write_card';
import {
    CARD_ADD_CLASS,
    CARD_CONTROL_CLASS,
    CardChip,
    EhrTagChips,
    useEhrNoteOptions,
} from '@apps/web/features/phoebe_chat_v3/ui/primitives/ehr_tag_picker_field';
import { useDebouncedValue } from '@apps/web/lib/use_debounced_value';
import { cn } from '@apps/web/lib/utils';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

type Method = (typeof EHR_NOTE_METHODS)[number];
type Person = EhrPersonNoteEditor['people'][number];

const METHOD_LABELS: Record<Method, string> = {
    phone: 'Phone',
    email: 'Email',
    in_person: 'In person',
    other: 'Other',
};
const SEARCH_LIMIT = 8;
const SEARCH_DEBOUNCE_MS = 250;

export function EhrPersonNoteApprovalView({
    editor,
    organizationId,
    outcome,
    receipt,
    status,
    submitting,
    onAllow,
    onDeny,
}: Readonly<{
    editor: EhrPersonNoteEditor;
    organizationId: string;
    outcome: PermissionOutcome;
    receipt?: WriteResultPresentation;
    status: PermissionStatus;
    submitting: boolean;
    onAllow: (argumentsOverride?: Record<string, unknown>) => void;
    onDeny: () => void;
}>): ReactNode {
    const [noteDate, setNoteDate] = useState(editor.note_date);
    const [method, setMethod] = useState<Method>(editor.method);
    const [tagIds, setTagIds] = useState<ReadonlyArray<string>>(editor.tag_ids);
    const [people, setPeople] = useState<ReadonlyArray<Person>>(editor.people);
    const options = useEhrNoteOptions(
        organizationId,
        editor.entity === 'clients'
            ? { client_id: editor.id }
            : { caregiver_id: editor.id },
    );
    const methods = (options.data?.methods ?? EHR_NOTE_METHODS).filter(
        (value): value is Method => value in METHOD_LABELS,
    );
    const failedSummary =
        receipt?.status === 'failed' ? receipt.summary : undefined;
    return (
        <CompactWriteCard
            card={editor.card}
            state={
                status === 'waiting'
                    ? {
                          status: 'pending',
                          disabled: submitting,
                          allowDisabled: noteDate === '',
                          onAllow: () =>
                              onAllow({
                                  ehr_person_note: {
                                      note_date: noteDate,
                                      method,
                                      tag_ids: tagIds,
                                      related_ids: people.map(
                                          (person) => person.id,
                                      ),
                                  },
                              }),
                          onDeny,
                      }
                    : {
                          status:
                              status === 'denied'
                                  ? 'denied'
                                  : outcome === 'pending'
                                    ? 'running'
                                    : failedSummary !== undefined
                                      ? 'failed'
                                      : outcome,
                          resultSummary: failedSummary,
                      }
            }
            notices={outcome === 'succeeded' ? receipt?.notices : []}
        >
            <p className="text-muted-foreground mt-1 mb-0 whitespace-pre-wrap">
                {editor.body}
            </p>
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
                <Input
                    aria-label="Date"
                    type="date"
                    value={noteDate}
                    disabled={submitting}
                    onChange={(event) => setNoteDate(event.target.value)}
                    className={cn(CARD_CONTROL_CLASS, 'w-auto')}
                />
                <Select
                    value={method}
                    disabled={submitting}
                    onValueChange={(value) => setMethod(value as Method)}
                >
                    <SelectTrigger
                        aria-label="Method"
                        className={cn(
                            CARD_CONTROL_CLASS,
                            'w-auto gap-1 py-0 data-[size=default]:h-7 [&_svg]:size-3',
                        )}
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {methods.map((value) => (
                            <SelectItem key={value} value={value}>
                                {METHOD_LABELS[value]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <EhrTagChips
                    options={options}
                    value={tagIds}
                    disabled={submitting}
                    onChange={setTagIds}
                />
                {people.map((person) => (
                    <CardChip
                        key={person.id}
                        label={person.name}
                        person={true}
                        disabled={submitting}
                        onRemove={() =>
                            setPeople(
                                people.filter(
                                    (chosen) => chosen.id !== person.id,
                                ),
                            )
                        }
                    />
                ))}
                <PersonPicker
                    organizationId={organizationId}
                    excludedIds={[editor.id, ...people.map((p) => p.id)]}
                    disabled={submitting}
                    onPick={(person) => setPeople([...people, person])}
                />
            </div>
        </CompactWriteCard>
    );
}

function PersonPicker({
    organizationId,
    excludedIds,
    disabled,
    onPick,
}: Readonly<{
    organizationId: string;
    excludedIds: ReadonlyArray<string>;
    disabled: boolean;
    onPick: (person: Person) => void;
}>): ReactNode {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    return (
        <Combobox
            open={open}
            onOpenChange={setOpen}
            placeholder="+ Person"
            disabled={disabled}
            searchValue={search}
            onSearchValueChange={setSearch}
            searchPlaceholder="Search clients and caregivers…"
            fullWidth={false}
            showCaret={false}
            matchTriggerWidth={false}
            triggerClassName={CARD_ADD_CLASS}
            contentClassName="w-72"
            commandProps={{ shouldFilter: false }}
            commandListProps={{ className: 'max-h-64' }}
        >
            {open && (
                <PersonResults
                    organizationId={organizationId}
                    search={search}
                    excludedIds={excludedIds}
                    onPick={(person) => {
                        onPick(person);
                        setOpen(false);
                    }}
                />
            )}
        </Combobox>
    );
}

// Mounted only while the picker is open, so the card fetches nothing idle.
function PersonResults({
    organizationId,
    search,
    excludedIds,
    onPick,
}: Readonly<{
    organizationId: string;
    search: string;
    excludedIds: ReadonlyArray<string>;
    onPick: (person: Person) => void;
}>): ReactNode {
    const query = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
    const clients = useApiQuery({
        path: '/organizations/{organization_id}/clients/search',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: { search: query, limit: SEARCH_LIMIT },
        reason: 'v3-ehr-note-people',
        select: (response) => response.clients,
    });
    const caregivers = useApiQuery({
        path: '/organizations/{organization_id}/caregivers',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: { search: query, per_page: SEARCH_LIMIT },
        reason: 'v3-ehr-note-people',
        select: (response) => response.caregivers,
    });
    const results = [
        ...(clients.data ?? []).map((client) => ({
            id: client.id,
            name: client.name,
            kind: 'Client',
        })),
        ...(caregivers.data ?? []).map((caregiver) => ({
            id: caregiver.id,
            name: caregiver.name,
            kind: 'Caregiver',
        })),
    ].filter((person) => !excludedIds.includes(person.id));
    return (
        <Fragment>
            <CommandEmpty className="text-muted-foreground text-v3-chat-meta py-6 text-center">
                {clients.isLoading || caregivers.isLoading
                    ? 'Searching…'
                    : 'No people found'}
            </CommandEmpty>
            {results.map((person) => (
                <CommandItem
                    key={person.id}
                    value={person.id}
                    onSelect={() =>
                        onPick({ id: person.id, name: person.name })
                    }
                    className="text-v3-chat py-2 text-left"
                >
                    {person.name}
                    <span className="text-muted-foreground ml-1.5">
                        {person.kind}
                    </span>
                </CommandItem>
            ))}
        </Fragment>
    );
}
